/**
 * Hangul document view (§18).
 *
 * Structurally the same continuous column as the Word view, and for the same
 * reason — Hangul's page breaks come from its own layout engine and font
 * metrics, so drawing page edges here would invent boundaries. What differs is
 * where formatting comes from: a run carries an id, and the header's shape
 * tables say what that id means. Resolving it per run is what makes a Hangul
 * document look like the document rather than like plain text.
 *
 * Typography note for §25: the file names the font it wanted (함초롬바탕,
 * 맑은 고딕). Those are usually absent outside Korea, so the stack falls back
 * to whatever Korean face the system has and the file-info screen reports the
 * substitution rather than pretending the rendering is faithful.
 */
import { useEffect, useMemo, useState } from 'react';
import type {
  CharShape, HwpxBlock, HwpxModel, HwpxParagraph, HwpxRun, HwpxTable, ParaShape,
} from '@moduviewer/handlers';
import { Icon } from '@moduviewer/design/icon';

export interface HwpxViewerProps {
  readonly model: HwpxModel;
  readonly editing: boolean;
  readonly search: string;
  readonly zoom: number;
  readonly onEditParagraph: (index: number, text: string) => void;
}

export function HwpxViewer({
  model, editing, search, zoom, onEditParagraph,
}: HwpxViewerProps): React.JSX.Element {
  const [active, setActive] = useState<number | null>(null);

  const imageUrls = useMemo(() => {
    const urls = new Map<string, string>();
    for (const [part, image] of model.images) {
      urls.set(part, URL.createObjectURL(new Blob([image.bytes as BlobPart], { type: image.contentType })));
    }
    return urls;
  }, [model.images]);

  useEffect(() => () => {
    for (const url of imageUrls.values()) URL.revokeObjectURL(url);
  }, [imageUrls]);

  useEffect(() => { if (!editing) setActive(null); }, [editing]);

  return (
    <div className="canvas doc-canvas">
      <article
        className="doc-page doc-page--hangul"
        style={{ ['--doc-zoom' as string]: String(zoom) }}
        lang="ko"
        aria-label="문서 내용"
      >
        {model.blocks.map((block, i) => (
          <Block
            key={block.kind === 'paragraph' ? `p${block.index}` : `t${i}-${block.start}`}
            block={block}
            model={model}
            editing={editing}
            active={active}
            search={search}
            imageUrls={imageUrls}
            onActivate={setActive}
            onCommit={(index, text) => { setActive(null); onEditParagraph(index, text); }}
            onCancel={() => setActive(null)}
          />
        ))}
      </article>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * blocks
 * ------------------------------------------------------------------ */

interface BlockProps {
  readonly block: HwpxBlock;
  readonly model: HwpxModel;
  readonly editing: boolean;
  readonly active: number | null;
  readonly search: string;
  readonly imageUrls: ReadonlyMap<string, string>;
  readonly onActivate: (index: number) => void;
  readonly onCommit: (index: number, text: string) => void;
  readonly onCancel: () => void;
}

function Block(props: BlockProps): React.JSX.Element {
  if (props.block.kind === 'table') return <Table {...props} block={props.block} />;
  return <Paragraph {...props} block={props.block} />;
}

function Table({ block, ...rest }: BlockProps & { block: HwpxTable }): React.JSX.Element {
  return (
    <div className="doc-table-wrap">
      <table className="doc-table">
        <tbody>
          {block.rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td
                  key={c}
                  colSpan={cell.colSpan > 1 ? cell.colSpan : undefined}
                  rowSpan={cell.rowSpan > 1 ? cell.rowSpan : undefined}
                >
                  {cell.paragraphs.map((p) => (
                    <Paragraph key={p.index} {...rest} block={p} />
                  ))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Paragraph(
  { block, model, editing, active, search, imageUrls, onActivate, onCommit, onCancel }:
  BlockProps & { block: HwpxParagraph },
): React.JSX.Element {
  const paragraph = block;
  const style = model.header.styles.get(paragraph.styleId ?? '');
  const paraShape: ParaShape | undefined = model.header.paraShapes.get(paragraph.paraPrId ?? '');
  const heading = style?.headingLevel ?? null;
  const changed = model.edits.has(paragraph.index);
  const canEdit = editing && paragraph.editable;

  if (active === paragraph.index) {
    return (
      <ParagraphEditor
        paragraph={paragraph}
        onCommit={(text) => onCommit(paragraph.index, text)}
        onCancel={onCancel}
      />
    );
  }

  const Tag = (heading ? `h${Math.min(heading + 1, 6)}` : 'p') as 'p';
  const empty = paragraph.runs.length === 0;

  const body = (
    <Tag
      className={[
        'doc-p',
        heading ? 'doc-p--heading' : '',
        changed ? 'doc-p--changed' : '',
        canEdit ? 'doc-p--editable' : '',
        empty ? 'doc-p--empty' : '',
      ].filter(Boolean).join(' ')}
      style={{
        textAlign: paraShape?.alignment ?? undefined,
        marginInlineStart: paraShape?.indentPx ? `${paraShape.indentPx}px` : undefined,
      }}
      data-paragraph={paragraph.index}
      // The style's Hangul name is genuinely useful here — "개요 2" tells a
      // Korean reader more about the paragraph than "heading 2" would.
      title={style?.name}
      {...(canEdit ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: () => onActivate(paragraph.index),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(paragraph.index); }
        },
      } : {})}
    >
      {paraShape?.numbered ? <span className="doc-bullet" aria-hidden="true">·</span> : null}
      {paragraph.runs.map((run, i) => (
        <Run
          key={i}
          run={run}
          shape={model.header.charShapes.get(
            run.kind === 'text' ? run.charPrId ?? '' : paragraph.charPrId ?? '',
          )}
          fonts={model.header.fonts}
          search={search}
          imageUrls={imageUrls}
        />
      ))}
      {empty ? ' ' : null}
    </Tag>
  );

  if (editing && !paragraph.editable && !empty) {
    return (
      <div className="doc-locked" title="그림·수식·개체가 있어 텍스트 편집을 지원하지 않습니다">
        {body}
        <Icon name="lock" size={13} className="doc-locked__mark" />
      </div>
    );
  }
  return body;
}

function ParagraphEditor(
  { paragraph, onCommit, onCancel }: {
    paragraph: HwpxParagraph;
    onCommit: (text: string) => void;
    onCancel: () => void;
  },
): React.JSX.Element {
  const [text, setText] = useState(paragraph.text);

  return (
    <div className="doc-edit">
      <textarea
        className="doc-edit__area"
        value={text}
        autoFocus
        lang="ko"
        aria-label={`문단 ${paragraph.index + 1} 편집`}
        onChange={(e) => {
          setText(e.target.value);
          e.target.style.height = 'auto';
          e.target.style.height = `${e.target.scrollHeight}px`;
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onCommit(text); }
        }}
        onBlur={() => onCommit(text)}
      />
      <p className="doc-edit__hint">
        <kbd>Enter</kbd> 적용 · <kbd>Shift</kbd>+<kbd>Enter</kbd> 줄바꿈 · <kbd>Esc</kbd> 취소
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * runs
 * ------------------------------------------------------------------ */

function Run(
  { run, shape, fonts, search, imageUrls }: {
    run: HwpxRun;
    shape: CharShape | undefined;
    fonts: ReadonlyMap<string, string>;
    search: string;
    imageUrls: ReadonlyMap<string, string>;
  },
): React.JSX.Element | null {
  if (run.kind === 'break') return <br />;
  if (run.kind === 'tab') return <span className="doc-tab" aria-hidden="true" />;

  if (run.kind === 'object') {
    // §19: name what is there and cannot be drawn, rather than leaving a gap
    // that reads as a parsing failure.
    return (
      <span className="doc-object" title={`${labelFor(run.what)} — 이 버전에서는 표시되지 않으며 저장 시 보존됩니다`}>
        <Icon name={run.what === 'equation' ? 'command' : 'shapes'} size={13} />
        {labelFor(run.what)}
      </span>
    );
  }

  if (run.kind === 'image') {
    const url = run.part ? imageUrls.get(run.part) : undefined;
    const width = run.widthPx || undefined;
    const height = run.heightPx || undefined;
    if (!url) {
      return (
        <span className="doc-image doc-image--missing" style={{ width, height }}>
          <Icon name="file-image" size={18} />
          <span>{run.alt || '표시할 수 없는 그림'}</span>
        </span>
      );
    }
    return <img className="doc-image" src={url} alt={run.alt} width={width} height={height} />;
  }

  const wanted = shape?.fontRef ? fonts.get(shape.fontRef) : undefined;
  const style: React.CSSProperties = {
    fontWeight: shape?.bold ? 700 : undefined,
    fontStyle: shape?.italic ? 'italic' : undefined,
    textDecoration: [
      shape?.underline ? 'underline' : '',
      shape?.strike ? 'line-through' : '',
    ].filter(Boolean).join(' ') || undefined,
    color: shape?.color ? `#${shape.color}` : undefined,
    fontSize: shape?.sizePx ? `${shape.sizePx}px` : undefined,
    // Name the requested face first; the fallback stack behind it is what
    // actually renders on a machine without Hangul's fonts (§25).
    fontFamily: wanted ? `"${wanted}", var(--font-ko)` : undefined,
  };

  return <span style={style}>{search ? highlight(run.text, search) : run.text}</span>;
}

function labelFor(what: string): string {
  const names: Readonly<Record<string, string>> = {
    equation: '수식', chart: '차트', ole: '개체', container: '묶음 개체', ctrl: '컨트롤',
  };
  return names[what] ?? what;
}

function highlight(text: string, needle: string): React.ReactNode {
  if (!needle) return text;
  const lower = text.toLowerCase();
  const target = needle.toLowerCase();
  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (;;) {
    const found = lower.indexOf(target, cursor);
    if (found < 0) break;
    if (found > cursor) out.push(text.slice(cursor, found));
    out.push(<mark key={found}>{text.slice(found, found + needle.length)}</mark>);
    cursor = found + needle.length;
  }
  if (out.length === 0) return text;
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
